import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import StepOneForm from "./StepOneForm";
import StepTwoForm from "./StepTwoForm";
import ProgressBar from "./ProgressBar";
import RegisterStep1 from "../../assets/image/RegisterStep1.png";
import RegisterStep2 from "../../assets/image/RegisterStep2.png";
import Logo from "../../assets/image/Logo.png";
import { API } from "../../shared/api/Api";
import { useLocation, useNavigate } from "react-router-dom";
import { handleFormValidationErrors, showToast } from "../../utils/utils";

const LANGUAGE_OPTIONS = ["Arabic", "English", "Spanish", "French", "German", "Chinese"];
const PHONE_REGEX = /^09\d{8}$/;
const NATIONAL_ID_REGEX = /^\d{11}$/;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,30}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const MIN_AGE_YEARS = 5;

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const imageVariants = {
    hidden: { x: 100 },
    visible: { x: 0 }
};

const RegistrationContainer = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        email: "",
        phone_number: "",
        first_name: "",
        last_name: "",
        birth_date: "",
        nationalId: "",
        password: "",
        password_confirmation: "",
        country: "",
        city: "",
        native_lang: "",
        image: null,
        gender: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeAction, setActiveAction] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [generalError, setGeneralError] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    const requiredFields = useMemo(() => ([
        { key: "first_name", label: "First name" },
        { key: "last_name", label: "Last name" },
        { key: "email", label: "Email" },
        { key: "phone_number", label: "Mobile phone" },
        { key: "gender", label: "Gender" },
        { key: "password", label: "Password" },
        { key: "password_confirmation", label: "Password confirmation" },
    ]), []);

    const optionalFields = useMemo(() => ([
        "country",
        "city",
        "native_lang",
        "nationalId",
        "birth_date",
        "image",
    ]), []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormErrors((prev) => {
            if (!prev[name] && !(name === "password" || name === "password_confirmation")) {
                return prev;
            }
            const updated = { ...prev };
            delete updated[name];
            if (name === "password" || name === "password_confirmation") {
                delete updated.password;
                delete updated.password_confirmation;
            }
            return updated;
        });
        if (generalError) {
            setGeneralError("");
        }
        if (name === "image") {
            const file = files && files.length > 0 ? files[0] : null;
            setFormData({ ...formData, [name]: file });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const clearErrorFields = (currentErrors, fields) => {
        const updated = { ...currentErrors };
        fields.forEach((key) => {
            delete updated[key];
        });
        return updated;
    };

    const validatePasswordComplexity = (password) => {
        if (!PASSWORD_REGEX.test(password)) {
            return "Password must be 8-30 characters and include uppercase, lowercase, number, and symbol.";
        }
        return null;
    };

    const validateStepOne = () => {
        const newErrors = {};

        requiredFields.forEach(({ key, label }) => {
            const value = formData[key];
            if (!value || (typeof value === "string" && value.trim() === "")) {
                newErrors[key] = `${label} is required.`;
            }
        });

        if (formData.first_name && (formData.first_name.trim().length < 2 || formData.first_name.trim().length > 50)) {
            newErrors.first_name = "First name must be between 2 and 50 characters.";
        }

        if (formData.last_name && (formData.last_name.trim().length < 2 || formData.last_name.trim().length > 50)) {
            newErrors.last_name = "Last name must be between 2 and 50 characters.";
        }

        if (formData.email && !EMAIL_REGEX.test(formData.email)) {
            newErrors.email = "Please enter a valid email address.";
        }

        if (formData.phone_number && !PHONE_REGEX.test(formData.phone_number)) {
            newErrors.phone_number = "Enter a valid phone number starting with 09 followed by 8 digits.";
        }

        if (formData.gender && !["male", "female"].includes(formData.gender)) {
            newErrors.gender = "Please select a valid gender option.";
        }

        if (formData.password) {
            const passwordError = validatePasswordComplexity(formData.password);
            if (passwordError) {
                newErrors.password = passwordError;
            }
        }

        if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = "Password and confirmation must match.";
            if (!newErrors.password) {
                newErrors.password = "Password and confirmation must match.";
            }
        }

        setFormErrors((prev) => ({
            ...clearErrorFields(prev, requiredFields.map(({ key }) => key).concat(["password", "password_confirmation"])),
            ...newErrors,
        }));

        if (Object.keys(newErrors).length > 0) {
            showToast("Missing information", "Please fix the highlighted fields.", "error");
            return false;
        }

        return true;
    };

    const validateOptionalFields = () => {
        const newErrors = {};
        const {
            nationalId,
            country,
            city,
            native_lang,
            birth_date,
            image,
        } = formData;

        if (nationalId && !NATIONAL_ID_REGEX.test(nationalId)) {
            newErrors.nationalId = "National ID must be exactly 11 digits.";
        }

        if (country && country.trim().length > 100) {
            newErrors.country = "Country must be 100 characters or fewer.";
        }

        if (city && city.trim().length > 100) {
            newErrors.city = "City must be 100 characters or fewer.";
        }

        if (native_lang && !LANGUAGE_OPTIONS.includes(native_lang)) {
            newErrors.native_lang = "Please choose a valid native language.";
        }

        if (birth_date) {
            const birthDateValue = new Date(birth_date);
            if (Number.isNaN(birthDateValue.getTime())) {
                newErrors.birth_date = "Please provide a valid birth date.";
            } else {
                const today = new Date();
                const minBirthDate = new Date(
                    today.getFullYear() - MIN_AGE_YEARS,
                    today.getMonth(),
                    today.getDate()
                );
                if (birthDateValue > minBirthDate) {
                    newErrors.birth_date = `Birth date must be at least ${MIN_AGE_YEARS} years ago.`;
                }
            }
        }

        if (image) {
            const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
            if (!allowedTypes.includes(image.type)) {
                newErrors.image = "Image must be a JPG, JPEG, PNG, or WEBP file.";
            } else if (image.size > MAX_IMAGE_SIZE_BYTES) {
                newErrors.image = "Image size must not exceed 2MB.";
            }
        }

        setFormErrors((prev) => ({
            ...clearErrorFields(prev, optionalFields),
            ...newErrors,
        }));

        if (Object.keys(newErrors).length > 0) {
            showToast("Validation error", "Please fix the highlighted optional fields.", "error");
            return false;
        }

        return true;
    };

    const submitRegistration = async (includeOptional = true) => {
        if (isSubmitting) return;

        try {
            setGeneralError("");
            setFormErrors((prev) => clearErrorFields(prev, requiredFields.map(({ key }) => key).concat(optionalFields)));

            if (!validateStepOne()) {
                return;
            }

            if (includeOptional && !validateOptionalFields()) {
                return;
            }

            setIsSubmitting(true);
            setActiveAction(includeOptional ? "submit" : "skip");

            const data = new FormData();
            requiredFields.forEach(({ key }) => {
                data.append(key, formData[key]);
            });

            if (includeOptional) {
                optionalFields.forEach((key) => {
                    const value = formData[key];
                    if (value !== null && value !== undefined && value !== "") {
                        data.append(key, value);
                    }
                });
            }

            const response = await axios.post(API.Admin.register, data, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // Navigate to verification page (or further actions)
            navigate("/Verification", { state: { type: 2, uuid: response.data.data.uuid, redirect: location?.state?.redirect } });
            showToast("Registration Completed", response.data.message, "success");
        } catch (error) {
            console.log(error)
            console.error("Registration failed:", error.response?.data || error.message);
            // Display error message to user if needed
            if (error.response) {
                const { status, data } = error.response;

                if (status === 400 || status === 422) {
                    const extractedFieldErrors = handleFormValidationErrors(error, setFormErrors, setGeneralError);

                    const fieldErrors = extractedFieldErrors || data?.data || data?.errors;
                    if (fieldErrors && typeof fieldErrors === 'object') {
                        const stepOneKeys = new Set(requiredFields.map(({ key }) => key));
                        const shouldReturnToStepOne = Object.keys(fieldErrors).some((key) => stepOneKeys.has(key));
                        if (shouldReturnToStepOne) {
                            setStep(1);
                        }
                    }

                    const validationMessage = Array.isArray(data?.message) ? data.message.join(" ") : (data?.message || "Validation error occurred.");
                    // handleFormValidationErrors already calls setGeneralError, but we call it here for redundancy
                    setGeneralError(validationMessage);
                    showToast("Validation error", validationMessage, "error");
                } else {
                    const message = data?.message || "Registration failed. Please try again.";
                    setGeneralError(message);
                    showToast("Registration Failed", message, "error");
                }
            } else {
                const fallbackMessage = "Registration failed. Please check your connection and try again.";
                setGeneralError(fallbackMessage);
                showToast("Registration Failed", fallbackMessage, "error");
            }
        } finally {
            setIsSubmitting(false);
            setActiveAction(null);
        }
    };

    const handleSubmit = () => submitRegistration(true);
    const handleSkipSubmission = () => submitRegistration(false);

    const nextStep = () => {
        if (validateStepOne()) {
            setStep(step + 1);
        }
    };

    const prevStep = () => setStep(step - 1);


    return (
        <motion.div
            className="flex flex-col lg:flex-row min-h-screen overflow-hidden"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Left Form Section */}
            <div className="flex flex-col items-center w-full lg:w-[40%] bg-white px-4 sl:px-14 py-2 p-0 ll:p-12 h-screen overflow-y-auto">
                <motion.div
                    className="flex flex-col justify-between h-screen w-full max-w-[493px]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <AnimatePresence mode='wait'>
                        {step === 1 && (
                            <motion.div
                                key="step1-header"
                                initial={{ y: -20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 20, opacity: 0 }}
                                className="flex flex-col items-center space-y-2 sl:space-y-3 ll:space-y-4 text-center"
                            >
                                <motion.img
                                    className="h-16 w-16 sm:h-16 sm:w-16 sl:h-24 sl:w-24 ll:h-32 ll:w-32"
                                    src={Logo}
                                    alt="Logo"
                                    whileHover={{ rotate: -2, scale: 1.05 }}
                                />
                                <h1 className="text-lg sl:text-2xl ll:text-3xl font-bold text-primary">Create a new account</h1>
                                <p className="text-sm sl:text-md ll:text-lg text-secondary">
                                    Language Village, Where Every Language Finds a Home
                                </p>
                                <motion.div className="text-sm text-secondary space-x-1 flex flex-row">
                                    <span>
                                        Already have an account?
                                    </span>
                                    <motion.div className="flex flex-row" whileHover={{ scale: 1.3 }}>
                                        <button
                                            onClick={() => navigate("/Login", { state: { redirect: location?.state?.redirect } })}
                                            className="text-primary underline hover:text-blue-600"
                                        >
                                            Login
                                        </button>
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence mode='wait'>
                        <motion.div
                            key={step}
                            initial={{ x: step === 1 ? 100 : -100, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: step === 1 ? -100 : 100, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300 }}
                            className="w-full"
                        >
                            {step === 1 ? (
                                <StepOneForm
                                    formData={formData}
                                    handleChange={handleChange}
                                    nextStep={nextStep}
                                    formErrors={formErrors}
                                    generalError={generalError}
                                />
                            ) : (
                                <StepTwoForm
                                    formData={formData}
                                    handleChange={handleChange}
                                    handleSubmit={handleSubmit}
                                    handleSkip={handleSkipSubmission}
                                    isSubmitting={isSubmitting}
                                    activeAction={activeAction}
                                    formErrors={formErrors}
                                    generalError={generalError}
                                />
                            )}
                        </motion.div>
                    </AnimatePresence>

                    <ProgressBar step={step} nextStep={nextStep} prevStep={prevStep} />

                </motion.div>
            </div>

            {/* Right Image Section */}
            <motion.div
                className="hidden lg:flex lg:w-[60%] relative h-screen bg-gray-100"
                variants={imageVariants}
            >
                <img
                    src={step === 1 ? RegisterStep1 : RegisterStep2}
                    alt="Registration"
                    className="w-full h-full object-cover"
                />
                <motion.div
                    className="absolute bottom-4 w-full text-center text-white text-sm lg:text-base px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <p className="mb-2">
                        By creating this account, you agree to our{' '}
                        <a href="#" className="border-b border-white">terms of service</a>
                    </p>
                    <p>Language Village 2024</p>
                </motion.div>
            </motion.div>
        </motion.div>
    );

    // return (
    //     <div className="flex flex-col lg:flex-row min-h-screen overflow-hidden">
    //         {/* Left Form Section */}
    //         <div className="flex flex-col items-center w-full lg:w-[40%] bg-white px-6 py-6 lg:p-[110px] h-screen overflow-y-auto">
    //             <div className="flex flex-col justify-between h-full">
    //                 {step === 1 && (
    //                     <div className="flex flex-col items-center space-y-6 text-center">
    //                         <img className="h-20 w-20 lg:h-[138px] lg:w-[138px]" src={Logo} alt="Logo" />
    //                         <h1 className="text-2xl lg:text-3xl font-bold text-[#134F98]">Create a new account</h1>
    //                         <p className="text-sm lg:text-base text-[#B7B7B7]">
    //                             Language Village, Where Every Language Finds a Home
    //                         </p>
    //                         <div className="capitalize">
    //                             <span className="text-sm text-[#B7B7B7]">
    //                                 or if you already have an account,
    //                             </span>
    //                             <a href="/Login" className="text-blue-500 text-sm border-b border-blue-500 ml-1">
    //                                 login
    //                             </a>
    //                         </div>
    //                     </div>
    //                 )}
    //                 {step === 1 ? (
    //                     <div className="w-full lg:w-[493px] lg:h-[529px]">
    //                         <StepOneForm formData={formData} handleChange={handleChange} nextStep={nextStep} />
    //                         <ProgressBar step={step} nextStep={nextStep} />
    //                     </div>
    //                 ) : (
    //                     <div className="w-full lg:w-[493px] lg:h-[529px] flex-1">
    //                         <StepTwoForm
    //                             formData={formData}
    //                             handleChange={handleChange}
    //                             handleSubmit={handleSubmit}
    //                             skipStep={skipStep}
    //                             prevStep={prevStep}
    //                         />
    //                         <ProgressBar step={step} nextStep={nextStep} prevStep={prevStep} />
    //                     </div>
    //                 )}
    //             </div>
    //         </div>

    //         {/* Right Image Section (hidden on small & mid screens) */}
    //         <div className="hidden lg:flex lg:w-[60%] relative h-screen"> {/* Changed to h-screen */}
    //             <img
    //                 src={step === 1 ? RegisterStep1 : RegisterStep2}
    //                 alt="Registration"
    //                 className="w-full h-full object-cover"
    //             />
    //             <div className="absolute bottom-4 text-center text-white text-sm lg:text-base">
    //                 <span>By creating this account, you are agreeing</span>
    //                 <a href="#" className="border-b border-white ml-1">
    //                     to our terms of service.
    //                 </a>
    //                 <p className="mt-2">Language Village 2024</p>
    //             </div>
    //         </div>
    //     </div>
    // );
};

export default RegistrationContainer;
